/**
 * Warm Minimalist Home Article - Interactive JavaScript
 * Handles scroll-driven dial rotation, room transformation,
 * email capture modal, and product hover previews
 */

(function () {
  'use strict';

  const isDev =
    window.location.hostname.includes('myshopify.com') || window.location.hostname === 'localhost';
  const error = isDev ? console.error.bind(console) : function () {};
  const warn = isDev ? console.warn.bind(console) : function () {};

  const CONFIG = {
    dialRotationDegrees: 360,
    emailModalScrollPercent: 60,
    mobileStickyScrollPercent: 40,
    throttleDelay: 16, // ~60fps
    roomTransformationStart: 0.2, // 20% scroll
    roomTransformationEnd: 0.9, // 90% scroll
    wayLabels: [
      'Start',
      'Natural Materials',
      'Layered Textures',
      'Handcrafted Details',
      'Meaningful Collections',
      'Wabi-Sabi',
      'Complete',
    ],
  };

  const STATE = {
    emailModalShown: false,
    mobileStickyShown: false,
    currentWayIndex: 0,
    isScrolling: false,
    isMobile: window.innerWidth < 768,
  };

  const MUSIC_STATE = {
    isPlaying: false,
    currentTrackIndex: 0,
    audio: null,
    userInteracted: false,
    enabled: true,
  };

  function initMusic() {
    const musicToggle = document.getElementById('musicToggle');
    const musicStartButton = document.getElementById('musicStartButton');
    const musicDeclineButton = document.getElementById('musicDeclineButton');
    const audioElement = document.getElementById('backgroundMusic');

    if (!musicToggle || !audioElement) {
      error('Music player elements not found');
      return;
    }

    MUSIC_STATE.audio = audioElement;
    MUSIC_STATE.audio.volume = 0;
    let hasUnmuted = false;

    const musicEnabled = localStorage.getItem('backgroundMusicEnabled');
    if (musicEnabled === 'false') {
      MUSIC_STATE.enabled = false;
      musicToggle.setAttribute('aria-pressed', 'false');
      MUSIC_STATE.audio.pause();
    } else {
      loadTrack(0);
      MUSIC_STATE.enabled = true;
      MUSIC_STATE.isPlaying = true;

      const unmuteAudio = () => {
        if (!hasUnmuted && MUSIC_STATE.enabled) {
          hasUnmuted = true;
          let currentVolume = 0;
          const fadeInterval = setInterval(() => {
            if (currentVolume < 0.3) {
              currentVolume += 0.01;
              MUSIC_STATE.audio.volume = Math.min(currentVolume, 0.3);
            } else {
              clearInterval(fadeInterval);
            }
          }, 50);

          window.removeEventListener('scroll', unmuteAudio);
          window.removeEventListener('click', unmuteAudio);
          window.removeEventListener('touchstart', unmuteAudio);
          window.removeEventListener('keydown', unmuteAudio);
        }
      };

      window.addEventListener('scroll', unmuteAudio, { once: true, passive: true });
      window.addEventListener('click', unmuteAudio, { once: true });
      window.addEventListener('touchstart', unmuteAudio, { once: true, passive: true });
      window.addEventListener('keydown', unmuteAudio, { once: true });
    }

    musicToggle.addEventListener('click', function () {
      if (MUSIC_STATE.isPlaying) {
        pauseMusic();
        MUSIC_STATE.enabled = false;
        musicToggle.setAttribute('aria-pressed', 'false');
        localStorage.setItem('backgroundMusicEnabled', 'false');
      } else {
        MUSIC_STATE.enabled = true;

        if (!MUSIC_STATE.audio.src || MUSIC_STATE.audio.src === '') {
          loadTrack(0);
        }

        playMusic();
        musicToggle.setAttribute('aria-pressed', 'true');
        localStorage.setItem('backgroundMusicEnabled', 'true');
      }
    });

    if (musicStartButton) {
      musicStartButton.addEventListener('click', function () {
        MUSIC_STATE.userInteracted = true;
        MUSIC_STATE.enabled = true;

        if (!MUSIC_STATE.audio.src || MUSIC_STATE.audio.src === '') {
          loadTrack(0);
        }

        playMusic();
        musicToggle.setAttribute('aria-pressed', 'true');
        localStorage.setItem('backgroundMusicEnabled', 'true');
        hideMusicOverlay();
      });
    }

    if (musicDeclineButton) {
      musicDeclineButton.addEventListener('click', function () {
        MUSIC_STATE.enabled = false;
        musicToggle.setAttribute('aria-pressed', 'false');
        localStorage.setItem('backgroundMusicEnabled', 'false');
        hideMusicOverlay();
      });
    }

    MUSIC_STATE.audio.addEventListener('ended', function () {
      nextTrack();
    });

    MUSIC_STATE.audio.addEventListener('error', function (e) {
      error('Audio playback error:', e);
      nextTrack();
    });
  }

  function loadTrack(index) {
    if (!MUSIC_STATE.audio) return;

    if (!window.MUSIC_ASSET_URLS || !Array.isArray(window.MUSIC_ASSET_URLS)) {
      error('MUSIC_ASSET_URLS not found or invalid');
      return;
    }

    if (index < 0 || index >= window.MUSIC_ASSET_URLS.length) {
      error('Invalid track index:', index);
      return;
    }

    const trackUrl = window.MUSIC_ASSET_URLS[index];
    if (!trackUrl) {
      error('Music asset URL not found for index:', index);
      return;
    }

    MUSIC_STATE.audio.src = trackUrl;
    MUSIC_STATE.currentTrackIndex = index;
  }

  /**
   * Play music with 3-second fade in
   */
  function playMusic() {
    if (!MUSIC_STATE.audio || !MUSIC_STATE.enabled) return;

    if (!MUSIC_STATE.audio.src || MUSIC_STATE.audio.src === '') {
      warn('Play attempted but no audio source loaded');
      return;
    }

    MUSIC_STATE.audio.volume = 0;

    MUSIC_STATE.audio
      .play()
      .then(() => {
        MUSIC_STATE.isPlaying = true;
        // Fade in to target volume over 3 seconds
        fadeIn(MUSIC_STATE.audio, 0.3, 3000);
      })
      .catch((err) => {
        MUSIC_STATE.isPlaying = false;
        error('Error playing music:', err);
      });
  }

  /**
   * Pause music with 3-second fade out
   */
  function pauseMusic() {
    if (!MUSIC_STATE.audio) return;

    fadeOut(MUSIC_STATE.audio, 3000).then(function () {
      MUSIC_STATE.audio.pause();
      MUSIC_STATE.isPlaying = false;
    });
  }

  /**
   * Fade audio volume in
   * @param {HTMLAudioElement} audio - Audio element
   * @param {number} targetVolume - Target volume (0-1)
   * @param {number} duration - Fade duration in milliseconds
   */
  function fadeIn(audio, targetVolume, duration) {
    if (!audio) return Promise.resolve();

    const startVolume = 0;
    const volumeChange = targetVolume - startVolume;
    const steps = 50; // Number of volume updates
    const stepDuration = duration / steps;
    const stepSize = volumeChange / steps;

    let currentStep = 0;

    return new Promise((resolve) => {
      const fadeInterval = setInterval(() => {
        currentStep++;
        const newVolume = startVolume + stepSize * currentStep;

        if (currentStep >= steps || newVolume >= targetVolume) {
          audio.volume = targetVolume;
          clearInterval(fadeInterval);
          resolve();
        } else {
          audio.volume = newVolume;
        }
      }, stepDuration);
    });
  }

  /**
   * Fade audio volume out
   * @param {HTMLAudioElement} audio - Audio element
   * @param {number} duration - Fade duration in milliseconds
   */
  function fadeOut(audio, duration) {
    if (!audio) return Promise.resolve();

    const startVolume = audio.volume;
    const steps = 50; // Number of volume updates
    const stepDuration = duration / steps;
    const stepSize = startVolume / steps;

    let currentStep = 0;

    return new Promise((resolve) => {
      const fadeInterval = setInterval(() => {
        currentStep++;
        const newVolume = startVolume - stepSize * currentStep;

        if (currentStep >= steps || newVolume <= 0) {
          audio.volume = 0;
          clearInterval(fadeInterval);
          resolve();
        } else {
          audio.volume = newVolume;
        }
      }, stepDuration);
    });
  }

  function nextTrack() {
    if (!window.MUSIC_ASSET_URLS || window.MUSIC_ASSET_URLS.length === 0) {
      error('No music playlist available');
      return;
    }

    const nextIndex = (MUSIC_STATE.currentTrackIndex + 1) % window.MUSIC_ASSET_URLS.length;
    loadTrack(nextIndex);

    if (MUSIC_STATE.isPlaying && MUSIC_STATE.enabled) {
      playMusic();
    }
  }

  function hideMusicOverlay() {
    const overlay = document.getElementById('musicStartOverlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
  }

  function throttle(func, delay) {
    let lastCall = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        return func(...args);
      }
    };
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function mapRange(value, inMin, inMax, outMin, outMax) {
    if (inMax === inMin) return outMin; // Prevent division by zero
    return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
  }

  function getScrollProgress() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    return scrollHeight > 0 ? scrollTop / scrollHeight : 0;
  }

  function updateScrollProgressVar() {
    const progress = getScrollProgress();
    document.documentElement.style.setProperty('--scroll-progress', progress);
  }

  const dialPointer = document.getElementById('dial-pointer');
  const dialLabel = document.getElementById('dial-label');

  let dialScrollHint = null;
  if (dialLabel) {
    dialScrollHint = document.createElement('div');
    dialScrollHint.className = 'dial-scroll-hint';
    dialScrollHint.innerHTML = `
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" transform="rotate(90 12 12)"/>
      </svg>
      <span>Scroll Down</span>
    `;
    dialLabel.parentElement.appendChild(dialScrollHint);
  }

  function rotateDial() {
    if (!dialPointer) return;

    const progress = getScrollProgress();
    const rotation = progress * CONFIG.dialRotationDegrees;

    dialPointer.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
  }

  function updateDialLabel() {
    if (!dialLabel) return;

    const progress = getScrollProgress();
    const labelIndex = Math.floor(progress * (CONFIG.wayLabels.length - 1));

    if (labelIndex !== STATE.currentWayIndex) {
      STATE.currentWayIndex = labelIndex;
      const labelText = CONFIG.wayLabels[labelIndex] || 'Start';
      dialLabel.textContent = labelText;
      dialLabel.setAttribute('aria-live', 'polite');

      if (dialScrollHint) {
        if (labelText === 'Start') {
          dialScrollHint.classList.add('visible');
        } else {
          dialScrollHint.classList.remove('visible');
        }
      }
    }
  }

  const warmRoomElement = document.querySelector('.warm-room');

  function transformRoom() {
    if (!warmRoomElement) return;

    const progress = getScrollProgress();

    const transformProgress = mapRange(
      progress,
      CONFIG.roomTransformationStart,
      CONFIG.roomTransformationEnd,
      0,
      1
    );

    const opacity = clamp(transformProgress, 0, 1);
    warmRoomElement.style.opacity = opacity;

    if (opacity >= 0.95) {
      document.body.setAttribute('data-scroll-stage', 'complete');
    } else {
      document.body.removeAttribute('data-scroll-stage');
    }
  }

  function initSectionTracking() {
    const waySections = document.querySelectorAll('.way-section');
    if (!waySections.length) return;

    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const wayNumber = entry.target.dataset.way;
          if (wayNumber && dialLabel) {
            const labelText = CONFIG.wayLabels[parseInt(wayNumber, 10)] || 'Start';
            dialLabel.textContent = `Way ${wayNumber}: ${labelText}`;
          }
        }
      });
    }, observerOptions);

    waySections.forEach((section) => observer.observe(section));
  }

  const emailModal = document.getElementById('email-modal');
  const emailModalClose = document.querySelector('.email-modal-close');
  const emailDismiss = document.querySelector('.email-dismiss');
  const emailModalOverlay = document.querySelector('.email-modal-overlay');
  let escapeKeyHandler = null;

  function checkEmailModal() {
    if (STATE.emailModalShown || !emailModal) return;

    const progress = getScrollProgress();
    const scrollPercent = progress * 100;

    if (scrollPercent >= CONFIG.emailModalScrollPercent) {
      showEmailModal();
    }
  }

  function showEmailModal() {
    if (!emailModal) return;

    STATE.emailModalShown = true;
    emailModal.removeAttribute('hidden');

    const emailInput = emailModal.querySelector('input[type="email"]');
    if (emailInput) {
      setTimeout(() => emailInput.focus(), 100);
    }

    try {
      localStorage.setItem('warmMinimalist_emailModalShown', 'true');
    } catch (_e) {
      // LocalStorage not available, ignore
    }
  }

  function hideEmailModal() {
    if (!emailModal) return;
    emailModal.setAttribute('hidden', '');
  }

  function initEmailModal() {
    try {
      if (localStorage.getItem('warmMinimalist_emailModalShown') === 'true') {
        STATE.emailModalShown = true;
      }
    } catch (_e) {
      // LocalStorage not available, ignore
    }

    if (emailModalClose) {
      emailModalClose.addEventListener('click', hideEmailModal);
    }

    if (emailDismiss) {
      emailDismiss.addEventListener('click', hideEmailModal);
    }

    if (emailModalOverlay) {
      emailModalOverlay.addEventListener('click', hideEmailModal);
    }

    if (!escapeKeyHandler) {
      escapeKeyHandler = (e) => {
        if (e.key === 'Escape' && emailModal && !emailModal.hasAttribute('hidden')) {
          hideEmailModal();
        }
      };
      document.addEventListener('keydown', escapeKeyHandler);
    }
  }

  const mobileStickyCTA = document.getElementById('mobile-sticky-cta');

  function checkMobileStickyCTA() {
    if (!STATE.isMobile || STATE.mobileStickyShown || !mobileStickyCTA) return;

    const progress = getScrollProgress();
    const scrollPercent = progress * 100;

    if (scrollPercent >= CONFIG.mobileStickyScrollPercent) {
      STATE.mobileStickyShown = true;
      mobileStickyCTA.removeAttribute('hidden');
    }
  }

  let productPreviewCard = null;
  let productPreviewTimeout = null;
  let currentProductHandle = null;

  function createProductPreviewCard() {
    const card = document.createElement('div');
    card.className = 'product-preview-card';
    card.setAttribute('role', 'tooltip');
    card.setAttribute('aria-live', 'polite');
    const imageWrapper = document.createElement('div');
    imageWrapper.className = 'product-preview-image-wrapper';
    const img = document.createElement('img');
    img.className = 'product-preview-image';
    img.alt = '';
    imageWrapper.appendChild(img);

    const content = document.createElement('div');
    content.className = 'product-preview-content';
    const title = document.createElement('h4');
    title.className = 'product-preview-title';
    const price = document.createElement('p');
    price.className = 'product-preview-price';
    content.appendChild(title);
    content.appendChild(price);

    card.replaceChildren(imageWrapper, content);

    const style = document.createElement('style');
    style.textContent = `
      .product-preview-card {
        position: absolute;
        width: 280px;
        background: rgba(255, 255, 255, 0.95); /* Slightly transparent */
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 2px solid var(--border-light);
        border-radius: 8px;
        padding: var(--space-md);
        box-shadow: 0 8px 24px rgba(42, 26, 15, 0.15);
        z-index: 50;
        opacity: 0;
        transform: translateY(10px);
        transition: opacity 0.3s ease, transform 0.3s ease;
        pointer-events: none;
      }

      .product-preview-card.visible {
        opacity: 1;
        transform: translateY(0);
      }

      .product-preview-image-wrapper {
        width: 100%;
        height: 180px;
        overflow: hidden;
        border-radius: 4px;
        margin-bottom: var(--space-md);
        background: var(--warm-cream);
      }

      .product-preview-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .product-preview-title {
        font-family: var(--font-vintage);
        font-size: var(--font-size-base);
        color: var(--text-primary);
        margin-bottom: var(--space-xs);
      }

      .product-preview-price {
        font-family: var(--font-sans);
        font-size: var(--font-size-lg);
        font-weight: 600;
        color: var(--terracotta);
        margin-bottom: 0;
      }

      @media (max-width: 767px) {
        .product-preview-card {
          position: fixed;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%) translateY(10px);
          width: calc(100% - 32px);
          max-width: 320px;
        }

        .product-preview-card.visible {
          transform: translateX(-50%) translateY(0);
        }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(card);
    return card;
  }

  async function fetchProductData(productHandle) {
    if (!window.shopifyProductAPI) return null;
    try {
      const response = await fetch(`${window.shopifyProductAPI}${productHandle}.js`);
      if (!response.ok) throw new Error('Product not found');
      return await response.json();
    } catch (error) {
      warn('Failed to fetch product:', productHandle, error);
      return null;
    }
  }

  async function showProductPreview(linkElement, _event) {
    const productHandle = linkElement.dataset.product;
    if (!productHandle) return;

    currentProductHandle = productHandle;

    if (productPreviewTimeout) {
      clearTimeout(productPreviewTimeout);
      productPreviewTimeout = null;
    }

    if (!productPreviewCard) {
      productPreviewCard = createProductPreviewCard();
    }

    const productData = await fetchProductData(productHandle);

    if (productHandle !== currentProductHandle) return;

    if (!productData) return;

    const imageEl = productPreviewCard.querySelector('.product-preview-image');
    const titleEl = productPreviewCard.querySelector('.product-preview-title');
    const priceEl = productPreviewCard.querySelector('.product-preview-price');

    if (!imageEl || !titleEl || !priceEl) {
      error('Product preview card structure is invalid');
      return;
    }

    if (productData.featured_image) {
      imageEl.src = productData.featured_image;
      imageEl.alt = productData.title || 'Product image';
    }

    titleEl.textContent = productData.title || 'Unknown Product';

    if (productData.variants && productData.variants[0] && productData.variants[0].price) {
      const price = (productData.variants[0].price / 100).toFixed(2);
      priceEl.textContent = `$${price}`;
    } else {
      priceEl.textContent = 'Price unavailable';
    }

    if (!STATE.isMobile) {
      const rect = linkElement.getBoundingClientRect();
      const cardWidth = 280;
      const cardHeight = 300;

      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;

      let top = rect.bottom + scrollY + 8;
      let left = rect.left + scrollX;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (left + cardWidth > viewportWidth - 20) {
        left = viewportWidth - cardWidth - 20;
      }

      if (rect.bottom + cardHeight > viewportHeight) {
        top = rect.top + scrollY - cardHeight - 8;
      }

      productPreviewCard.style.top = `${top}px`;
      productPreviewCard.style.left = `${Math.max(20, left)}px`;
    }

    productPreviewCard.classList.add('visible');
  }

  function hideProductPreview() {
    if (!productPreviewCard) return;

    productPreviewTimeout = setTimeout(() => {
      productPreviewCard.classList.remove('visible');
    }, 200);
  }

  function initProductHoverPreviews() {
    const productLinks = document.querySelectorAll('.product-link[data-product]');

    productLinks.forEach((link) => {
      if (!STATE.isMobile) {
        link.addEventListener('mouseenter', (e) => showProductPreview(link, e));
        link.addEventListener('mouseleave', hideProductPreview);
      }

      link.addEventListener(
        'touchstart',
        (e) => {
          showProductPreview(link, e);

          if (productPreviewTimeout) {
            clearTimeout(productPreviewTimeout);
          }

          productPreviewTimeout = setTimeout(() => {
            hideProductPreview();
          }, 3000);
        },
        { passive: true }
      );
    });
  }

  const handleScroll = throttle(function () {
    updateScrollProgressVar();
    rotateDial();
    updateDialLabel();
    transformRoom();
    checkEmailModal();
    checkMobileStickyCTA();
  }, CONFIG.throttleDelay);

  const handleResize = throttle(function () {
    STATE.isMobile = window.innerWidth < 768;

    if (!STATE.isMobile && mobileStickyCTA) {
      mobileStickyCTA.setAttribute('hidden', '');
    }
  }, 200);

  function hideLoadingOverlay() {
    const loadingOverlay = document.querySelector('.loading-overlay');
    if (loadingOverlay) {
      setTimeout(() => {
        loadingOverlay.classList.add('hidden');
        setTimeout(() => {
          loadingOverlay.remove();
        }, 500);
      }, 1000);
    }
  }

  function init() {
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    initSectionTracking();
    initEmailModal();
    initProductHoverPreviews();
    initMusic();

    hideLoadingOverlay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
